// banking/src/pages/onboarding/corporate/components/RepresentativeCard.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { Representative } from '../CorporateOnboarding';

interface RepresentativeCardProps {
  representative: Representative;
  index: number;
  language: Language;
  onEdit: () => void;
  onDelete: () => void;
}

const RepresentativeCard: React.FC<RepresentativeCardProps> = ({
  representative,
  index,
  language,
  onEdit,
  onDelete,
}) => {
  const fullName = `${representative.firstName} ${representative.lastName} ${representative.secondLastName}`.trim();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Representative info */}
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-[#B3A369] font-semibold text-lg">
                {representative.firstName.charAt(0)}{representative.lastName.charAt(0)}
              </span>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">{fullName}</h3>
              <p className="text-xs text-gray-500">{representative.role}</p>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">{getTranslation('relationship_type_label', language)}:</span>
              <span className="ml-1 text-gray-900 font-medium">
                {getTranslation(`relationship_${representative.relationshipType.toLowerCase()}`, language)}
              </span>
            </div>

            <div>
              <span className="text-gray-500">{getTranslation('ownership_percentage_label', language)}:</span>
              <span className="ml-1 text-gray-900 font-medium">{representative.ownershipPercentage}%</span>
            </div>

            <div>
              <span className="text-gray-500">{getTranslation('document_type_label', language)}:</span>
              <span className="ml-1 text-gray-900 font-medium">
                {representative.documentType}
              </span>
            </div>

            <div>
              <span className="text-gray-500">{getTranslation('document_number_label', language)}:</span>
              <span className="ml-1 text-gray-900 font-medium">{representative.documentNumber}</span>
            </div>

            <div className="col-span-2">
              <span className="text-gray-500">{getTranslation('email_label', language)}:</span>
              <span className="ml-1 text-gray-900 font-medium">{representative.email}</span>
            </div>

            {representative.isUltimateBeneficiary && (
              <div className="col-span-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ {getTranslation('is_ultimate_beneficiary_label', language)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col space-y-2 ml-4">
          <button
            type="button"
            onClick={onEdit}
            className="px-3 py-1 text-xs font-medium text-[#B3A369] bg-[#F5F3E8] rounded hover:bg-blue-100 transition-colors"
          >
            {getTranslation('edit_representative', language)}
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
          >
            {getTranslation('delete_representative', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepresentativeCard;
